import { useState, useEffect } from 'react';
import { builderApi } from '../api';
import { fmtDate, DateInput } from '../utils';

export default function BuilderReports() {
  const [tab, setTab] = useState('project-cost');
  const [projectCost, setProjectCost] = useState([]);
  const [inventoryRpt, setInventoryRpt] = useState([]);
  const [purchasesRpt, setPurchasesRpt] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      builderApi.reportProjectCost(),
      builderApi.reportInventory(),
    ]).then(([pc, inv]) => { setProjectCost(pc); setInventoryRpt(inv); })
      .catch(e => setErr(e.message)).finally(() => setLoading(false));
  }, []);

  const loadPurchases = () => {
    setLoading(true);
    builderApi.reportPurchases(fromDate, toDate)
      .then(setPurchasesRpt).catch(e => setErr(e.message)).finally(() => setLoading(false));
  };

  useEffect(() => { if (tab === 'purchases') loadPurchases(); }, [tab]); // eslint-disable-line

  const fmt = (n) => n != null ? `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '₹0.00';
  const STATUS_COLORS = { ACTIVE: '#38a169', COMPLETED: '#3182ce', ON_HOLD: '#d69e2e' };

  const exportCsv = (headers, rows, filename) => {
    const lines = [headers.join(','), ...rows.map(r => headers.map(h => `"${r[h] ?? ''}"`).join(','))];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = filename; a.click();
  };

  const TABS = [
    { key: 'project-cost', label: 'Project Cost' },
    { key: 'inventory',    label: 'Inventory Stock' },
    { key: 'purchases',    label: 'Purchase History' },
  ];

  return (
    <div className="bld-card">
      <h2 style={{ margin: '0 0 16px', fontSize: 18, color: 'var(--text-primary)' }}>Reports</h2>

      {err && <div style={{ color: '#e53e3e', marginBottom: 12, fontSize: 13 }}>{err}</div>}

      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid var(--border-color)' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '8px 20px', fontSize: 13, fontWeight: 600,
            color: tab === t.key ? '#3182ce' : '#718096',
            borderBottom: tab === t.key ? '2px solid #3182ce' : '2px solid transparent',
            marginBottom: -2,
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {loading && <div style={{ color: '#718096', fontSize: 13 }}>Loading…</div>}

      {/* Project Cost Report */}
      {tab === 'project-cost' && !loading && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <button className="btn btn-outline" style={{ fontSize: 12 }} onClick={() =>
              exportCsv(['name','status','budget','material_cost','labour_cost'], projectCost, 'project-cost.csv')}>
              Export CSV
            </button>
          </div>
          {projectCost.length > 0 && (() => {
            const totalBudget = projectCost.reduce((s, r) => s + Number(r.budget || 0), 0);
            const totalMat = projectCost.reduce((s, r) => s + Number(r.material_cost || 0), 0);
            const totalLabour = projectCost.reduce((s, r) => s + Number(r.labour_cost || 0), 0);
            return (
              <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                {[
                  { label: 'Total Budget', value: fmt(totalBudget), color: '#3182ce' },
                  { label: 'Material Cost', value: fmt(totalMat), color: '#d69e2e' },
                  { label: 'Labour Cost (Work + Payments)', value: fmt(totalLabour), color: '#9f7aea' },
                  { label: 'Total Spent', value: fmt(totalMat + totalLabour), color: '#e53e3e' },
                ].map(s => (
                  <div key={s.label} style={{ flex: 1, background: 'var(--bg-secondary)', borderRadius: 8,
                    padding: '10px 14px', borderLeft: `4px solid ${s.color}` }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: 11, color: '#718096' }}>{s.label}</div>
                  </div>
                ))}
              </div>
            );
          })()}
          <table className="data-table">
            <thead>
              <tr><th>Project</th><th>Status</th><th>Budget</th><th>Material Cost</th><th>Labour Cost (Work+Payments)</th><th>Total Spent</th><th>Variance</th></tr>
            </thead>
            <tbody>
              {projectCost.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', color: '#718096', padding: 24 }}>No projects yet</td></tr>}
              {projectCost.map(r => {
                const spent = Number(r.material_cost || 0) + Number(r.labour_cost || 0);
                const variance = Number(r.budget || 0) - spent;
                return (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 600 }}>{r.name}</td>
                    <td><span style={{ background: STATUS_COLORS[r.status] || '#718096', color: '#fff',
                      padding: '2px 8px', borderRadius: 12, fontSize: 11 }}>{r.status}</span></td>
                    <td>{fmt(r.budget)}</td>
                    <td>{fmt(r.material_cost)}</td>
                    <td>{fmt(r.labour_cost)}</td>
                    <td style={{ fontWeight: 700 }}>{fmt(spent)}</td>
                    <td style={{ color: variance >= 0 ? '#38a169' : '#e53e3e', fontWeight: 600 }}>
                      {variance >= 0 ? '+' : ''}{fmt(variance)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {/* Inventory Report */}
      {tab === 'inventory' && !loading && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <button className="btn btn-outline" style={{ fontSize: 12 }} onClick={() =>
              exportCsv(['material_name','category','unit','quantity','total_purchased','total_issued'], inventoryRpt, 'inventory.csv')}>
              Export CSV
            </button>
          </div>
          <table className="data-table">
            <thead>
              <tr><th>Material</th><th>Category</th><th>Unit</th><th style={{ textAlign: 'right' }}>Purchased</th><th style={{ textAlign: 'right' }}>Issued</th><th style={{ textAlign: 'right' }}>Balance</th></tr>
            </thead>
            <tbody>
              {inventoryRpt.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: '#718096', padding: 24 }}>No inventory data</td></tr>}
              {inventoryRpt.map(r => {
                const bal = Number(r.quantity || 0);
                return (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 600 }}>{r.material_name}</td>
                    <td>{r.category || '—'}</td>
                    <td>{r.unit}</td>
                    <td style={{ textAlign: 'right' }}>{Number(r.total_purchased || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 })}</td>
                    <td style={{ textAlign: 'right' }}>{Number(r.total_issued || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 })}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: bal <= 0 ? '#e53e3e' : bal < 10 ? '#d69e2e' : '#38a169' }}>
                      {bal.toLocaleString('en-IN', { maximumFractionDigits: 3 })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {/* Purchase History Report */}
      {tab === 'purchases' && !loading && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'flex-end' }}>
            <div>
              <label className="form-label">From</label>
              <DateInput className="form-input" value={fromDate} onChange={e => setFromDate(e.target.value)} />
            </div>
            <div>
              <label className="form-label">To</label>
              <DateInput className="form-input" value={toDate} onChange={e => setToDate(e.target.value)} />
            </div>
            <button className="btn btn-primary" onClick={loadPurchases} style={{ fontSize: 13 }}>Apply</button>
            <button className="btn btn-outline" style={{ fontSize: 12, marginLeft: 'auto' }} onClick={() =>
              exportCsv(['purchase_date','supplier','invoice_no','project_name','total_amount'], purchasesRpt, 'purchases.csv')}>
              Export CSV
            </button>
          </div>
          {purchasesRpt.length > 0 && (
            <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: '10px 16px', marginBottom: 12,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: '#4a5568' }}>{purchasesRpt.length} purchase(s)</span>
              <span style={{ fontWeight: 700, fontSize: 16, color: '#3182ce' }}>
                Total: {fmt(purchasesRpt.reduce((s, r) => s + Number(r.total_amount || 0), 0))}
              </span>
            </div>
          )}
          <table className="data-table">
            <thead>
              <tr><th>Date</th><th>Supplier</th><th>Invoice</th><th>Project</th><th style={{ textAlign: 'right' }}>Amount</th></tr>
            </thead>
            <tbody>
              {purchasesRpt.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#718096', padding: 24 }}>No purchases found</td></tr>}
              {purchasesRpt.map(r => (
                <tr key={r.id}>
                  <td>{fmtDate(r.purchase_date)}</td>
                  <td>{r.supplier || '—'}</td>
                  <td>{r.invoice_no || '—'}</td>
                  <td>{r.project_name || '—'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(r.total_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
